import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Switch } from '../components/ui/switch';
import {
  Lightning,
  ArrowLeft,
  Upload,
  Sparkle,
  Target,
  Megaphone,
  Users,
  PaperPlaneTilt,
  CheckCircle,
  Plugs,
  PlugsConnected,
  X,
  SpinnerGap,
  Camera,
  FilePdf
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CreateCampaign() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Steps
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Upload state
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [businessNameInput, setBusinessNameInput] = useState('');

  // Analysis result
  const [analysis, setAnalysis] = useState(null);
  const [image1x1, setImage1x1] = useState(null);
  const [image9x16, setImage9x16] = useState(null);
  const [originalImage, setOriginalImage] = useState(null);
  const [aiGenerated, setAiGenerated] = useState(false);
  const [variants, setVariants] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(0);
  const [previewMode, setPreviewMode] = useState('grid'); // 'grid' | 'feed' | 'story'

  // Campaign config
  const [campaignName, setCampaignName] = useState('');
  const [campaignType, setCampaignType] = useState('engagement');
  const [headline, setHeadline] = useState('');
  const [primaryText, setPrimaryText] = useState('');
  const [budget, setBudget] = useState(10);
  const [dailyBudget, setDailyBudget] = useState(true);

  // Targeting
  const [ageMin, setAgeMin] = useState(18);
  const [ageMax, setAgeMax] = useState(65);
  const [genders, setGenders] = useState([1, 2]);
  const [locations, setLocations] = useState([{ key: 'IT', name: 'Italia', type: 'country', country_code: 'IT' }]);
  const [interests, setInterests] = useState([]);
  const [interestQuery, setInterestQuery] = useState('');
  const [interestResults, setInterestResults] = useState([]);
  const [interestSearching, setInterestSearching] = useState(false);
  const [locationQuery, setLocationQuery] = useState('');
  const [locationResults, setLocationResults] = useState([]);
  const [locationSearching, setLocationSearching] = useState(false);

  // Lead form
  const [includeEmail, setIncludeEmail] = useState(true);
  const [includePhone, setIncludePhone] = useState(true);
  const [qualifyingQuestion, setQualifyingQuestion] = useState('');

  // Meta OAuth
  const [metaPages, setMetaPages] = useState([]);
  const [metaAdAccounts, setMetaAdAccounts] = useState([]);
  const [selectedPage, setSelectedPage] = useState('');
  const [selectedAdAccount, setSelectedAdAccount] = useState('');
  const [metaToken, setMetaToken] = useState('');

  // Handle Meta callback — restore wizard state saved before redirect
  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    
    if (code && state) {
      // Restore wizard state from localStorage (saved before Facebook redirect)
      try {
        const saved = localStorage.getItem('vaionline_wizard_state');
        if (saved) {
          const s = JSON.parse(saved);
          if (s.analysis) setAnalysis(s.analysis);
          if (s.image1x1) setImage1x1(s.image1x1);
          if (s.image9x16) setImage9x16(s.image9x16);
          if (s.originalImage) setOriginalImage(s.originalImage);
          if (s.aiGenerated) setAiGenerated(true);
          if (s.variants) { setVariants(s.variants); setSelectedVariant(s.selectedVariant || 0); }
          if (s.headline) setHeadline(s.headline);
          if (s.primaryText) setPrimaryText(s.primaryText);
          if (s.campaignName) setCampaignName(s.campaignName);
          if (s.campaignType) setCampaignType(s.campaignType);
          if (s.budget) setBudget(s.budget);
          if (s.ageMin) setAgeMin(s.ageMin);
          if (s.ageMax) setAgeMax(s.ageMax);
          if (s.interests) setInterests(s.interests);
          if (s.locations) setLocations(s.locations);
          if (s.includeEmail !== undefined) setIncludeEmail(s.includeEmail);
          if (s.includePhone !== undefined) setIncludePhone(s.includePhone);
          if (s.qualifyingQuestion) setQualifyingQuestion(s.qualifyingQuestion);
          localStorage.removeItem('vaionline_wizard_state');
        }
      } catch (e) {
        console.warn('Could not restore wizard state:', e);
      }
      setStep(4); // Go to Meta step
      handleMetaCallback(code, state);
    }
  }, [searchParams]);

  const handleMetaCallback = async (code, state) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/meta/callback?code=${code}&state=${state}`
      );
      
      setMetaToken(response.data.access_token);
      setMetaPages(response.data.pages || []);
      setMetaAdAccounts(response.data.ad_accounts || []);
      
      // Auto-select first options
      if (response.data.pages?.length > 0) {
        setSelectedPage(response.data.pages[0].id);
      }
      if (response.data.ad_accounts?.length > 0) {
        setSelectedAdAccount(response.data.ad_accounts[0].id);
      }
      
      // Clear URL params
      window.history.replaceState({}, '', '/create');
    } catch (err) {
      setError('Errore nella connessione a Meta. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const handleMetaConnect = async () => {
    try {
      // Save wizard state to localStorage before leaving for Facebook
      const wizardState = {
        analysis, image1x1, image9x16, originalImage, aiGenerated,
        variants, selectedVariant,
        headline, primaryText, campaignName, campaignType, budget,
        ageMin, ageMax, interests, locations,
        includeEmail, includePhone, qualifyingQuestion
      };
      localStorage.setItem('vaionline_wizard_state', JSON.stringify(wizardState));
      
      const response = await axios.get(`${API_URL}/api/meta/auth-url`, {
        
        params: { frontend_origin: window.location.origin }
      });
      window.location.href = response.data.auth_url;
    } catch (err) {
      setError('Errore nel collegamento a Meta');
    }
  };

  const saveMetaConnection = async () => {
    if (!selectedPage || !selectedAdAccount || !metaToken) return;
    
    setLoading(true);
    try {
      const selectedPageData = metaPages.find(p => p.id === selectedPage);
      await axios.post(
        `${API_URL}/api/meta/connect`,
        {
          access_token: metaToken,
          page_id: selectedPage,
          ad_account_id: selectedAdAccount,
          page_name: selectedPageData?.name
        }
      );
      await refreshUser();
      setStep(4);
    } catch (err) {
      setError('Errore nel salvataggio della connessione');
    } finally {
      setLoading(false);
    }
  };

  // Search interests via Meta API
  const searchInterests = useCallback(async (query) => {
    if (!query || query.length < 2) { setInterestResults([]); return; }
    setInterestSearching(true);
    try {
      const res = await axios.get(`${API_URL}/api/meta/search-interests`, { params: { q: query } });
      setInterestResults(res.data || []);
    } catch { setInterestResults([]); }
    finally { setInterestSearching(false); }
  }, []);

  // Search locations via Meta API
  const searchLocations = useCallback(async (query) => {
    if (!query || query.length < 2) { setLocationResults([]); return; }
    setLocationSearching(true);
    try {
      const res = await axios.get(`${API_URL}/api/meta/search-locations`, { params: { q: query } });
      setLocationResults(res.data || []);
    } catch { setLocationResults([]); }
    finally { setLocationSearching(false); }
  }, []);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => { if (interestQuery) searchInterests(interestQuery); }, 400);
    return () => clearTimeout(t);
  }, [interestQuery, searchInterests]);

  useEffect(() => {
    const t = setTimeout(() => { if (locationQuery) searchLocations(locationQuery); }, 400);
    return () => clearTimeout(t);
  }, [locationQuery, searchLocations]);

  const handleFileDrop = useCallback((e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer?.files?.[0] || e.target.files?.[0];
    if (droppedFile) {
      const isImage = droppedFile.type.startsWith('image/');
      const isPDF = droppedFile.type === 'application/pdf' || droppedFile.name.toLowerCase().endsWith('.pdf');
      
      if (isImage || isPDF) {
        setFile(droppedFile);
        if (isImage) {
          setPreview(URL.createObjectURL(droppedFile));
        } else {
          // For PDF, show a placeholder
          setPreview('pdf');
        }
      } else {
        setError('Formato non supportato. Usa immagini (JPG, PNG, WEBP) o PDF.');
      }
    }
  }, []);

  const analyzeFlyer = async () => {
    if (!file) return;
    
    setAnalyzing(true);
    setError('');
    setScanProgress(0);

    // Simulate scanning progress
    const progressInterval = setInterval(() => {
      setScanProgress(prev => Math.min(prev + Math.random() * 15, 90));
    }, 500);

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (businessNameInput.trim()) {
        formData.append('business_name', businessNameInput.trim());
      }

      const response = await axios.post(
        `${API_URL}/api/analyze-flyer`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 180000 // 3 min timeout for AI processing
        }
      );

      clearInterval(progressInterval);
      setScanProgress(100);

      setAnalysis(response.data.analysis);
      setOriginalImage(response.data.original_image);
      setAiGenerated(response.data.ai_generated || false);
      
      // Handle variants (3 layout options)
      const receivedVariants = response.data.variants || [];
      if (receivedVariants.length > 0) {
        setVariants(receivedVariants);
        setSelectedVariant(0);
        setImage1x1(receivedVariants[0].image_1x1);
        setImage9x16(receivedVariants[0].image_9x16);
      } else {
        setImage1x1(response.data.image_1x1);
        setImage9x16(response.data.image_9x16);
      }
      
      setHeadline(response.data.analysis.headline || '');
      setPrimaryText(response.data.analysis.primary_text || '');
      setQualifyingQuestion(response.data.analysis.qualifying_question || '');
      setCampaignName(response.data.analysis.business_name || 'Nuova Campagna');

      setTimeout(() => setStep(2), 500);
    } catch (err) {
      clearInterval(progressInterval);
      const detail = err.response?.data?.detail || '';
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        setError('Timeout: l\'analisi sta impiegando troppo. Riprova con un file più piccolo.');
      } else if (detail) {
        setError(`Errore: ${detail}`);
      } else {
        setError('Errore nell\'analisi. Controlla la connessione e riprova.');
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const createCampaign = async () => {
    setLoading(true);
    setError('');

    try {
      const campaignData = {
        name: campaignName,
        campaign_type: campaignType,
        budget_cents: Math.round(budget * 100),
        daily_budget: dailyBudget,
        target_audience: {
          age_min: ageMin,
          age_max: ageMax,
          genders: genders,
          locations: locations,
          interests: interests.length > 0 ? interests : null
        },
        lead_form_config: campaignType === 'lead_generation' ? {
          include_email: includeEmail,
          include_phone: includePhone,
          qualifying_question: qualifyingQuestion
        } : null,
        generated_headline: headline,
        generated_primary_text: primaryText,
        image_1x1_base64: image1x1,
        image_9x16_base64: image9x16,
        meta_page_id: selectedPage || user?.meta_account?.page_id,
        meta_ad_account_id: selectedAdAccount || user?.meta_account?.ad_account_id
      };

      const response = await axios.post(
        `${API_URL}/api/campaigns`,
        campaignData
      );

      // Publish immediately
      await axios.post(
        `${API_URL}/api/campaigns/${response.data.id}/publish`
      );

      setStep(5);
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore nella creazione della campagna');
    } finally {
      setLoading(false);
    }
  };

  const isMetaConnected = user?.meta_account || (metaToken && selectedPage && selectedAdAccount);

  return (
    <div className="min-h-screen bg-[#05050A]">
      {/* Header */}
      <header className="glass border-b border-white/10 px-6 py-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              data-testid="back-button"
              onClick={() => navigate('/dashboard')}
              className="p-2 rounded-lg hover:bg-white/5 text-[#A1A1AA] hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#FF2A85] flex items-center justify-center neon-glow">
                <Lightning weight="fill" className="w-6 h-6 text-white" />
              </div>
              <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Unbounded' }}>
                Nuova Campagna
              </span>
            </div>
          </div>

          {/* Progress */}
          <div className="hidden md:flex items-center gap-2">
            {[1, 2, 3, 4, 5].map((s) => (
              <div
                key={s}
                className={`w-8 h-1 rounded-full transition-colors ${
                  s <= step ? 'bg-[#FF2A85]' : 'bg-white/10'
                }`}
              />
            ))}
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        {error && (
          <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 flex items-center gap-3">
            <X className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
            <button onClick={() => setError('')} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Step 1: Upload */}
        {step === 1 && (
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Unbounded' }}>
                Carica il tuo Volantino
              </h1>
              <p className="text-[#A1A1AA]">
                Scatta una foto, carica un'immagine o un PDF del volantino da trasformare in campagna
              </p>
            </div>

            {/* Mobile Camera Button */}
            <div className="mb-4 md:hidden">
              <label className="flex items-center justify-center gap-2 w-full py-4 bg-[#FF2A85] hover:bg-[#FF4D9B] text-white font-semibold rounded-xl cursor-pointer btn-glow transition-all">
                <Camera weight="fill" className="w-5 h-5" />
                Scatta una Foto
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleFileDrop}
                  className="hidden"
                />
              </label>
            </div>

            <div
              data-testid="upload-zone"
              onDrop={handleFileDrop}
              onDragOver={(e) => e.preventDefault()}
              className={`upload-zone border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer relative overflow-hidden ${
                preview ? 'border-[#FF2A85]' : 'border-white/20 hover:border-[#FF2A85]'
              }`}
              onClick={() => document.getElementById('file-input').click()}
            >
              <input
                id="file-input"
                type="file"
                accept="image/*,.pdf,application/pdf"
                onChange={handleFileDrop}
                className="hidden"
              />

              {preview ? (
                <div className="relative">
                  {preview === 'pdf' ? (
                    <div className="flex flex-col items-center justify-center py-8">
                      <div className="w-20 h-20 rounded-2xl bg-[#FF2A85]/10 flex items-center justify-center mb-4">
                        <FilePdf weight="fill" className="w-10 h-10 text-[#FF2A85]" />
                      </div>
                      <p className="font-semibold">{file?.name}</p>
                      <p className="text-sm text-[#A1A1AA] mt-1">PDF caricato</p>
                    </div>
                  ) : (
                    <img
                      src={preview}
                      alt="Preview"
                      className="max-h-80 mx-auto rounded-lg"
                    />
                  )}
                  {analyzing && (
                    <div className="absolute inset-0 bg-black/70 rounded-lg flex flex-col items-center justify-center">
                      <div className="relative w-full h-full">
                        <div
                          className="scanning-line absolute left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#FF2A85] to-transparent"
                        />
                      </div>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <Sparkle weight="fill" className="w-8 h-8 text-[#FF2A85] animate-pulse mb-3" />
                        <p className="text-white font-semibold mb-2">Analisi AI in corso...</p>
                        <div className="w-48 h-2 bg-white/10 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#FF2A85] transition-all duration-300"
                            style={{ width: `${scanProgress}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <div className="w-20 h-20 rounded-2xl bg-[#FF2A85]/10 flex items-center justify-center mx-auto mb-6">
                    <Upload weight="duotone" className="w-10 h-10 text-[#FF2A85]" />
                  </div>
                  <p className="text-lg font-semibold mb-2">
                    Trascina qui il tuo volantino
                  </p>
                  <p className="text-sm text-[#A1A1AA]">
                    oppure clicca per selezionare un file
                  </p>
                  <p className="text-xs text-[#A1A1AA] mt-4">
                    Formati supportati: JPG, PNG, WEBP, PDF
                  </p>
                </div>
              )}
            </div>

            {preview && !analyzing && (
              <div className="mt-6 space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm text-[#A1A1AA]">
                    Nome dell'attività <span className="text-[#FF2A85]/60">(opzionale — evita errori AI)</span>
                  </Label>
                  <Input
                    data-testid="business-name-input"
                    type="text"
                    placeholder="es. Hambilux, Euronics, Pizzeria Da Mario..."
                    value={businessNameInput}
                    onChange={(e) => setBusinessNameInput(e.target.value)}
                    className="bg-black border-white/10 text-white placeholder:text-[#A1A1AA]/50"
                  />
                </div>
                <div className="flex gap-4">
                  <Button
                    data-testid="change-image-button"
                    variant="outline"
                    onClick={() => {
                      setFile(null);
                      setPreview(null);
                    }}
                    className="flex-1 border-white/10 hover:border-white/20"
                  >
                    Cambia immagine
                  </Button>
                  <Button
                    data-testid="analyze-button"
                    onClick={analyzeFlyer}
                    className="flex-1 bg-[#FF2A85] hover:bg-[#FF4D9B] text-white btn-glow"
                  >
                    <Sparkle weight="fill" className="w-4 h-4 mr-2" />
                    Analizza con AI
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Edit Content & Select Variant */}
        {step === 2 && (
          <div className="space-y-8">
            {/* Variant Selection */}
            {variants.length > 1 && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold" style={{ fontFamily: 'Unbounded' }}>
                    Scegli il Layout
                  </h2>
                  {aiGenerated && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#E4FF1A]/10 text-[#E4FF1A] text-xs font-medium">
                      <Sparkle weight="fill" className="w-3 h-3" />
                      Generata con AI
                    </span>
                  )}
                </div>
                <p className="text-sm text-[#A1A1AA] mb-5">Stesso contenuto, 3 stili diversi. Tocca per scegliere.</p>

                <div className="grid grid-cols-3 gap-4">
                  {variants.map((v, idx) => (
                    <button
                      key={v.id}
                      data-testid={`variant-${v.id}`}
                      onClick={() => {
                        setSelectedVariant(idx);
                        setImage1x1(v.image_1x1);
                        setImage9x16(v.image_9x16);
                      }}
                      className={`relative rounded-xl overflow-hidden border-2 transition-all group ${
                        selectedVariant === idx
                          ? 'border-[#FF2A85] ring-2 ring-[#FF2A85]/30 scale-[1.02]'
                          : 'border-white/10 hover:border-white/30'
                      }`}
                    >
                      <img
                        src={`data:image/jpeg;base64,${v.image_1x1}`}
                        alt={v.label}
                        className="w-full aspect-square object-cover"
                      />
                      <div className={`absolute bottom-0 left-0 right-0 p-2.5 text-center ${
                        selectedVariant === idx
                          ? 'bg-[#FF2A85]'
                          : 'bg-black/70 group-hover:bg-black/80'
                      }`}>
                        <span className="text-sm font-bold text-white">{v.label}</span>
                      </div>
                      {selectedVariant === idx && (
                        <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#FF2A85] flex items-center justify-center">
                          <CheckCircle weight="fill" className="w-5 h-5 text-white" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Preview Modes */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <h2 className="text-xl font-bold" style={{ fontFamily: 'Unbounded' }}>Anteprima</h2>
                <div className="flex bg-black/40 border border-white/10 rounded-lg p-0.5 ml-auto">
                  {[
                    { id: 'grid', label: 'Immagini' },
                    { id: 'feed', label: 'Feed' },
                    { id: 'story', label: 'Stories' }
                  ].map((mode) => (
                    <button
                      key={mode.id}
                      data-testid={`preview-mode-${mode.id}`}
                      onClick={() => setPreviewMode(mode.id)}
                      className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                        previewMode === mode.id
                          ? 'bg-[#FF2A85] text-white'
                          : 'text-[#A1A1AA] hover:text-white'
                      }`}
                    >
                      {mode.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Grid view */}
              {previewMode === 'grid' && (
                <div className="grid md:grid-cols-2 gap-6">
                  {image1x1 && (
                    <div className="bg-[#0C0D14] border border-white/10 rounded-xl p-4">
                      <p className="text-xs text-[#A1A1AA] mb-2 font-medium">FEED 1:1</p>
                      <img src={`data:image/jpeg;base64,${image1x1}`} alt="1:1" className="w-full rounded-lg" />
                    </div>
                  )}
                  {image9x16 && (
                    <div className="bg-[#0C0D14] border border-white/10 rounded-xl p-4 flex flex-col">
                      <p className="text-xs text-[#A1A1AA] mb-2 font-medium">STORIES 9:16</p>
                      <img src={`data:image/jpeg;base64,${image9x16}`} alt="9:16" className="max-h-[420px] rounded-lg object-contain mx-auto" />
                    </div>
                  )}
                </div>
              )}

              {/* Facebook Feed Mockup */}
              {previewMode === 'feed' && image1x1 && (
                <div className="max-w-lg mx-auto">
                  <div className="bg-white rounded-xl overflow-hidden shadow-xl">
                    {/* FB Header */}
                    <div className="px-4 py-3 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold text-sm">
                        {(analysis?.business_name || 'A').charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{analysis?.business_name || campaignName}</p>
                        <p className="text-xs text-gray-500">Sponsorizzato</p>
                      </div>
                    </div>
                    {/* Primary Text */}
                    <div className="px-4 pb-2">
                      <p className="text-sm text-gray-800">{primaryText || analysis?.primary_text || ''}</p>
                    </div>
                    {/* Image */}
                    <img src={`data:image/jpeg;base64,${image1x1}`} alt="Ad" className="w-full" />
                    {/* CTA area */}
                    <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500 uppercase">{analysis?.business_name || ''}</p>
                        <p className="text-sm font-semibold text-gray-900">{headline || analysis?.headline || ''}</p>
                      </div>
                      <div className="bg-gray-200 px-4 py-2 rounded-md">
                        <span className="text-sm font-semibold text-gray-700">
                          {campaignType === 'lead_generation' ? 'Iscriviti' : 'Scopri di piu'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <p className="text-center text-xs text-[#A1A1AA] mt-3">Anteprima Feed Facebook — aspetto indicativo</p>
                </div>
              )}

              {/* Instagram Stories Mockup */}
              {previewMode === 'story' && image9x16 && (
                <div className="max-w-xs mx-auto">
                  <div className="relative rounded-2xl overflow-hidden shadow-xl bg-black" style={{ aspectRatio: '9/16' }}>
                    <img src={`data:image/jpeg;base64,${image9x16}`} alt="Story" className="w-full h-full object-cover" />
                    {/* IG Story header */}
                    <div className="absolute top-0 left-0 right-0 p-3">
                      <div className="w-full h-0.5 bg-white/30 rounded-full mb-3">
                        <div className="w-1/3 h-full bg-white rounded-full" />
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-500 via-red-500 to-yellow-500 p-0.5">
                          <div className="w-full h-full rounded-full bg-black flex items-center justify-center text-white text-xs font-bold">
                            {(analysis?.business_name || 'A').charAt(0)}
                          </div>
                        </div>
                        <span className="text-white text-xs font-semibold">{analysis?.business_name || campaignName}</span>
                        <span className="text-white/50 text-xs">Sponsorizzato</span>
                      </div>
                    </div>
                    {/* Swipe up */}
                    <div className="absolute bottom-0 left-0 right-0 p-4 text-center bg-gradient-to-t from-black/60 to-transparent">
                      <div className="flex flex-col items-center gap-1">
                        <svg className="w-5 h-5 text-white animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
                        <span className="text-white text-xs font-semibold">Scorri verso l'alto</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-center text-xs text-[#A1A1AA] mt-3">Anteprima Instagram Stories — aspetto indicativo</p>
                </div>
              )}
            </div>

            {/* Right side: Configuration */}
            <div className="grid lg:grid-cols-2 gap-8">
              <div className="space-y-6">
                <h2 className="text-xl font-bold" style={{ fontFamily: 'Unbounded' }}>
                  Configura Campagna
                </h2>

                {/* Campaign Name */}
                <div className="space-y-2">
                  <Label className="text-sm text-[#A1A1AA]">Nome Campagna</Label>
                  <Input
                    data-testid="campaign-name-input"
                    value={campaignName}
                    onChange={(e) => setCampaignName(e.target.value)}
                    className="bg-black border-white/10"
                    placeholder="Nome della campagna"
                  />
                </div>

                {/* Campaign Type */}
                <div className="space-y-3">
                  <Label className="text-sm text-[#A1A1AA]">Tipo di Campagna</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      data-testid="engagement-type-button"
                      onClick={() => setCampaignType('engagement')}
                      className={`p-4 rounded-xl border text-left transition-all ${
                        campaignType === 'engagement'
                          ? 'border-[#FF2A85] bg-[#FF2A85]/10'
                          : 'border-white/10 hover:border-white/20'
                      }`}
                    >
                      <Megaphone weight="fill" className={`w-6 h-6 mb-2 ${
                        campaignType === 'engagement' ? 'text-[#FF2A85]' : 'text-[#A1A1AA]'
                      }`} />
                      <p className="font-semibold">Interazione</p>
                      <p className="text-xs text-[#A1A1AA] mt-1">Like, commenti, condivisioni</p>
                    </button>
                    <button
                      data-testid="lead-gen-type-button"
                      onClick={() => setCampaignType('lead_generation')}
                      className={`p-4 rounded-xl border text-left transition-all ${
                        campaignType === 'lead_generation'
                          ? 'border-[#0055FF] bg-[#0055FF]/10'
                          : 'border-white/10 hover:border-white/20'
                      }`}
                    >
                      <Users weight="fill" className={`w-6 h-6 mb-2 ${
                        campaignType === 'lead_generation' ? 'text-[#0055FF]' : 'text-[#A1A1AA]'
                      }`} />
                      <p className="font-semibold">Lead Generation</p>
                      <p className="text-xs text-[#A1A1AA] mt-1">Raccogli contatti</p>
                    </button>
                  </div>
                </div>

                {/* Copy */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-sm text-[#A1A1AA]">Headline</Label>
                    <Input
                      data-testid="headline-input"
                      value={headline}
                      onChange={(e) => setHeadline(e.target.value)}
                      className="bg-black border-white/10"
                      placeholder="Headline accattivante"
                      maxLength={40}
                    />
                    <p className="text-xs text-[#A1A1AA] text-right">{headline.length}/40</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-[#A1A1AA]">Testo Primario</Label>
                    <textarea
                      data-testid="primary-text-input"
                      value={primaryText}
                      onChange={(e) => setPrimaryText(e.target.value)}
                      className="w-full h-24 px-3 py-2 bg-black border border-white/10 rounded-md text-white placeholder:text-[#A1A1AA] focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF]"
                      placeholder="Testo dell'annuncio"
                      maxLength={125}
                    />
                    <p className="text-xs text-[#A1A1AA] text-right">{primaryText.length}/125</p>
                  </div>
                </div>

                {/* Lead Form Config */}
                {campaignType === 'lead_generation' && (
                  <div className="space-y-4 p-4 bg-[#0055FF]/5 border border-[#0055FF]/20 rounded-xl">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Users weight="fill" className="w-4 h-4 text-[#0055FF]" />
                      Configurazione Modulo Lead
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm">Richiedi Email</Label>
                        <Switch
                          data-testid="include-email-switch"
                          checked={includeEmail}
                          onCheckedChange={setIncludeEmail}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label className="text-sm">Richiedi Telefono</Label>
                        <Switch
                          data-testid="include-phone-switch"
                          checked={includePhone}
                          onCheckedChange={setIncludePhone}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm text-[#A1A1AA]">Domanda Qualificante</Label>
                        <Input
                          data-testid="qualifying-question-input"
                          value={qualifyingQuestion}
                          onChange={(e) => setQualifyingQuestion(e.target.value)}
                          className="bg-black border-white/10"
                          placeholder="Es: Quando vorresti essere contattato?"
                        />
                      </div>
                    </div>
                  </div>
                )}

                <Button
                  data-testid="next-to-targeting"
                  onClick={() => setStep(3)}
                  className="w-full bg-[#FF2A85] hover:bg-[#FF4D9B] text-white btn-glow"
                >
                  Continua: Targeting
                </Button>
              </div>

              {/* Analysis Info */}
              <div>
                {analysis && (
                  <div className="bg-[#0C0D14] border border-white/10 rounded-xl p-5">
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <Sparkle weight="fill" className="w-4 h-4 text-[#E4FF1A]" />
                      Analisi AI
                    </h3>
                    <div className="space-y-2 text-sm">
                      <p><span className="text-[#A1A1AA]">Attivita:</span> {analysis.business_name}</p>
                      <p><span className="text-[#A1A1AA]">Offerta:</span> {analysis.offer}</p>
                      <p><span className="text-[#A1A1AA]">Stile:</span> {analysis.style}</p>
                      <p><span className="text-[#A1A1AA]">Target suggerito:</span> {analysis.target_suggestion}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Targeting */}
        {step === 3 && (
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <Target weight="fill" className="w-12 h-12 text-[#FF2A85] mx-auto mb-4" />
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Unbounded' }}>
                Definisci il Pubblico
              </h1>
              <p className="text-[#A1A1AA]">
                Configura il target per la tua campagna
              </p>
            </div>

            <div className="bg-[#0C0D14] border border-white/10 rounded-2xl p-6 space-y-6">
              {/* Age Range */}
              <div className="space-y-4">
                <Label className="text-sm text-[#A1A1AA]">Fascia d'età: {ageMin} - {ageMax} anni</Label>
                <div className="px-2">
                  <Slider
                    data-testid="age-slider"
                    value={[ageMin, ageMax]}
                    min={18}
                    max={65}
                    step={1}
                    onValueChange={([min, max]) => {
                      setAgeMin(min);
                      setAgeMax(max);
                    }}
                    className="[&_[role=slider]]:bg-[#FF2A85]"
                  />
                </div>
              </div>

              {/* Gender */}
              <div className="space-y-3">
                <Label className="text-sm text-[#A1A1AA]">Genere</Label>
                <div className="flex gap-3">
                  {[
                    { value: [1, 2], label: 'Tutti' },
                    { value: [1], label: 'Uomini' },
                    { value: [2], label: 'Donne' }
                  ].map((option) => (
                    <button
                      key={option.label}
                      data-testid={`gender-${option.label.toLowerCase()}`}
                      onClick={() => setGenders(option.value)}
                      className={`flex-1 py-2 px-4 rounded-lg border text-sm font-medium transition-all ${
                        JSON.stringify(genders) === JSON.stringify(option.value)
                          ? 'border-[#FF2A85] bg-[#FF2A85]/10 text-white'
                          : 'border-white/10 text-[#A1A1AA] hover:border-white/20'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Location — Autocomplete */}
              <div className="space-y-2">
                <Label className="text-sm text-[#A1A1AA]">Località</Label>
                {/* Selected locations */}
                {locations.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {locations.map((loc, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#0055FF]/20 text-[#0055FF] text-sm font-medium"
                      >
                        {typeof loc === 'object' ? `${loc.name}${loc.type !== 'country' ? ` (${loc.type})` : ''}` : loc}
                        <button
                          onClick={() => setLocations(locations.filter((_, i) => i !== idx))}
                          className="hover:text-white"
                        >
                          <X weight="bold" className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="relative">
                  <Input
                    data-testid="location-input"
                    value={locationQuery}
                    onChange={(e) => setLocationQuery(e.target.value)}
                    className="bg-black border-white/10"
                    placeholder="Cerca citta, regione o nazione..."
                  />
                  {locationSearching && (
                    <SpinnerGap weight="bold" className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA] animate-spin" />
                  )}
                  {locationResults.length > 0 && (
                    <div className="absolute z-20 w-full mt-1 bg-[#0C0D14] border border-white/10 rounded-xl max-h-56 overflow-y-auto shadow-xl">
                      {locationResults.map((loc, idx) => {
                        const alreadyAdded = locations.some(l => (typeof l === 'object' ? l.key : l) === loc.key);
                        return (
                          <button
                            key={idx}
                            disabled={alreadyAdded}
                            onClick={() => {
                              if (!alreadyAdded) {
                                setLocations([...locations, loc]);
                                setLocationQuery('');
                                setLocationResults([]);
                              }
                            }}
                            className={`w-full text-left px-4 py-2.5 text-sm hover:bg-white/5 transition-colors border-b border-white/5 last:border-0 ${alreadyAdded ? 'opacity-40' : ''}`}
                          >
                            <span className="font-medium">{loc.name}</span>
                            <span className="text-[#A1A1AA] ml-2">
                              {loc.type === 'city' ? 'Citta' : loc.type === 'region' ? 'Regione' : loc.type === 'country' ? 'Nazione' : loc.type}
                              {loc.country_name && loc.type !== 'country' ? `, ${loc.country_name}` : ''}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                {!user?.meta_account && (
                  <p className="text-xs text-[#A1A1AA]">Connetti Meta per cercare località specifiche. Default: Italia.</p>
                )}
              </div>

              {/* Interests — Autocomplete */}
              <div className="space-y-2">
                <Label className="text-sm text-[#A1A1AA]">Interessi (opzionale)</Label>
                {/* Selected interests */}
                {interests.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {interests.map((interest, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FF2A85]/20 text-[#FF2A85] text-sm font-medium"
                      >
                        {interest.name}
                        <button
                          onClick={() => setInterests(interests.filter((_, i) => i !== idx))}
                          className="hover:text-white"
                        >
                          <X weight="bold" className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="relative">
                  <Input
                    data-testid="interests-input"
                    value={interestQuery}
                    onChange={(e) => setInterestQuery(e.target.value)}
                    className="bg-black border-white/10"
                    placeholder="Cerca interessi su Meta (es: pizza, fitness, moda...)"
                  />
                  {interestSearching && (
                    <SpinnerGap weight="bold" className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA] animate-spin" />
                  )}
                  {interestResults.length > 0 && (
                    <div className="absolute z-20 w-full mt-1 bg-[#0C0D14] border border-white/10 rounded-xl max-h-56 overflow-y-auto shadow-xl">
                      {interestResults.map((item, idx) => {
                        const alreadyAdded = interests.some(i => i.id === item.id);
                        const audience = item.audience_size_lower_bound && item.audience_size_upper_bound
                          ? `${(item.audience_size_lower_bound / 1000000).toFixed(1)}M - ${(item.audience_size_upper_bound / 1000000).toFixed(1)}M`
                          : '';
                        return (
                          <button
                            key={idx}
                            disabled={alreadyAdded || interests.length >= 10}
                            onClick={() => {
                              if (!alreadyAdded && interests.length < 10) {
                                setInterests([...interests, { id: item.id, name: item.name }]);
                                setInterestQuery('');
                                setInterestResults([]);
                              }
                            }}
                            className={`w-full text-left px-4 py-2.5 text-sm hover:bg-white/5 transition-colors border-b border-white/5 last:border-0 ${alreadyAdded ? 'opacity-40' : ''}`}
                          >
                            <span className="font-medium">{item.name}</span>
                            {audience && <span className="text-[#A1A1AA] ml-2 text-xs">{audience} persone</span>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                {!user?.meta_account && (
                  <p className="text-xs text-[#A1A1AA]">Connetti Meta per cercare interessi validati. Senza interessi, Meta usa Advantage+ automatico.</p>
                )}
                {interests.length === 0 && user?.meta_account && (
                  <p className="text-xs text-[#A1A1AA]">Nessun interesse selezionato = Meta usa Advantage+ (targeting automatico AI).</p>
                )}
              </div>

              {/* Budget */}
              <div className="space-y-4 pt-4 border-t border-white/10">
                <div className="flex items-center justify-between">
                  <Label className="text-sm text-[#A1A1AA]">Budget: €{budget}</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#A1A1AA]">Giornaliero</span>
                    <Switch
                      data-testid="daily-budget-switch"
                      checked={dailyBudget}
                      onCheckedChange={setDailyBudget}
                    />
                  </div>
                </div>
                <Slider
                  data-testid="budget-slider"
                  value={[budget]}
                  min={5}
                  max={500}
                  step={5}
                  onValueChange={([val]) => setBudget(val)}
                  className="[&_[role=slider]]:bg-[#FF2A85]"
                />
                <p className="text-xs text-[#A1A1AA]">
                  {dailyBudget ? `€${budget}/giorno` : `€${budget} budget totale`}
                </p>
              </div>
            </div>

            <div className="mt-6 flex gap-4">
              <Button
                variant="outline"
                onClick={() => setStep(2)}
                className="flex-1 border-white/10 hover:border-white/20"
              >
                Indietro
              </Button>
              <Button
                data-testid="next-to-meta"
                onClick={() => setStep(4)}
                className="flex-1 bg-[#FF2A85] hover:bg-[#FF4D9B] text-white btn-glow"
              >
                Continua: Connetti Meta
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Meta Connection */}
        {step === 4 && (
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-2xl bg-[#0055FF]/10 flex items-center justify-center mx-auto mb-4">
                {isMetaConnected ? (
                  <PlugsConnected weight="fill" className="w-8 h-8 text-[#E4FF1A]" />
                ) : (
                  <Plugs weight="fill" className="w-8 h-8 text-[#0055FF]" />
                )}
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Unbounded' }}>
                {isMetaConnected ? 'Account Meta Connesso' : 'Connetti Meta Ads'}
              </h1>
              <p className="text-[#A1A1AA]">
                {isMetaConnected 
                  ? 'Il tuo account è pronto per pubblicare campagne'
                  : 'Collega il tuo account Facebook per pubblicare la campagna'
                }
              </p>
            </div>

            {isMetaConnected ? (
              <div className="bg-[#0C0D14] border border-[#E4FF1A]/30 rounded-2xl p-6">
                <div className="flex items-center gap-4 mb-6">
                  <CheckCircle weight="fill" className="w-8 h-8 text-[#E4FF1A]" />
                  <div>
                    <p className="font-semibold">Account collegato</p>
                    <p className="text-sm text-[#A1A1AA]">
                      {metaPages.find(p => p.id === selectedPage)?.name || user?.meta_account?.page_name || 'Pagina selezionata'}
                    </p>
                  </div>
                </div>

                {/* Always show Page/Account info — editable if just connected, read-only summary otherwise */}
                {metaPages.length > 0 ? (
                  <div className="space-y-4 mb-6">
                    <div className="space-y-2">
                      <Label className="text-sm text-[#A1A1AA]">Pagina Facebook</Label>
                      <select
                        data-testid="page-select"
                        value={selectedPage}
                        onChange={(e) => setSelectedPage(e.target.value)}
                        className="w-full px-3 py-2 bg-black border border-white/10 rounded-md text-white"
                      >
                        {metaPages.map((page) => (
                          <option key={page.id} value={page.id}>{page.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm text-[#A1A1AA]">Ad Account</Label>
                      <select
                        data-testid="ad-account-select"
                        value={selectedAdAccount}
                        onChange={(e) => setSelectedAdAccount(e.target.value)}
                        className="w-full px-3 py-2 bg-black border border-white/10 rounded-md text-white"
                      >
                        {metaAdAccounts.map((acc) => (
                          <option key={acc.id} value={acc.id}>{acc.name || acc.id}</option>
                        ))}
                      </select>
                    </div>
                    
                    {!user?.meta_account && (
                      <Button
                        data-testid="save-meta-connection"
                        onClick={saveMetaConnection}
                        disabled={loading}
                        className="w-full bg-[#0055FF] hover:bg-[#0066FF] text-white btn-blue-glow"
                      >
                        {loading ? (
                          <SpinnerGap className="w-4 h-4 animate-spin mr-2" />
                        ) : null}
                        Salva e Continua
                      </Button>
                    )}
                  </div>
                ) : user?.meta_account ? (
                  <div className="space-y-3 mb-6 bg-black/30 rounded-xl p-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-[#A1A1AA]">Pagina</span>
                      <span className="text-sm font-medium">{user.meta_account.page_name || user.meta_account.page_id}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-[#A1A1AA]">Ad Account</span>
                      <span className="text-sm font-medium">{user.meta_account.ad_account_id}</span>
                    </div>
                    <p className="text-xs text-[#A1A1AA] mt-2">
                      Per cambiare pagina o account, riconnettiti con Facebook.
                    </p>
                    <Button
                      variant="outline"
                      onClick={handleMetaConnect}
                      className="w-full mt-2 border-white/10 hover:border-white/20 text-sm"
                    >
                      <Plugs weight="bold" className="w-3 h-3 mr-2" />
                      Cambia Pagina / Account
                    </Button>
                  </div>
                ) : null}

                <Button
                  data-testid="publish-campaign"
                  onClick={createCampaign}
                  disabled={loading}
                  className="w-full bg-[#FF2A85] hover:bg-[#FF4D9B] text-white btn-glow"
                >
                  {loading ? (
                    <SpinnerGap className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <PaperPlaneTilt weight="fill" className="w-4 h-4 mr-2" />
                  )}
                  Pubblica Campagna su {metaPages.find(p => p.id === selectedPage)?.name || user?.meta_account?.page_name || 'Meta'}
                </Button>
                <p className="text-xs text-[#A1A1AA] text-center mt-2">
                  La campagna verrà creata in stato PAUSED. Potrai attivarla dal Meta Ads Manager.
                </p>
              </div>
            ) : (
              <div className="bg-[#0C0D14] border border-white/10 rounded-2xl p-8 text-center">
                <p className="text-[#A1A1AA] mb-6">
                  Per pubblicare la tua campagna su Facebook e Instagram, devi prima collegare il tuo account Meta Business.
                </p>
                <Button
                  data-testid="connect-meta-button"
                  onClick={handleMetaConnect}
                  disabled={loading}
                  className="bg-[#0055FF] hover:bg-[#0066FF] text-white btn-blue-glow px-8"
                >
                  {loading ? (
                    <SpinnerGap className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Plugs weight="bold" className="w-4 h-4 mr-2" />
                  )}
                  Connetti con Facebook
                </Button>
              </div>
            )}

            <div className="mt-6">
              <Button
                variant="outline"
                onClick={() => setStep(3)}
                className="w-full border-white/10 hover:border-white/20"
              >
                Indietro
              </Button>
            </div>
          </div>
        )}

        {/* Step 5: Success */}
        {step === 5 && (
          <div className="max-w-lg mx-auto text-center">
            <div className="w-20 h-20 rounded-full bg-[#E4FF1A]/10 flex items-center justify-center mx-auto mb-6 pulse-neon">
              <CheckCircle weight="fill" className="w-10 h-10 text-[#E4FF1A]" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Unbounded' }}>
              Campagna Pubblicata!
            </h1>
            <p className="text-[#A1A1AA] mb-8">
              La tua campagna "{campaignName}" è stata creata con successo e inviata a Meta Ads in stato PAUSED. Puoi attivarla dal tuo Business Manager.
            </p>
            <div className="flex gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/create')}
                className="flex-1 border-white/10 hover:border-white/20"
              >
                Crea un'altra
              </Button>
              <Button
                data-testid="go-to-dashboard"
                onClick={() => navigate('/dashboard')}
                className="flex-1 bg-[#FF2A85] hover:bg-[#FF4D9B] text-white btn-glow"
              >
                Vai alla Dashboard
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
